import _ from 'lodash';
import type { Client, Fax, FhbfMeasure } from '../../../common/types/client';
import type { DecryptedItem } from '../../../common/types/items';
import filter from '../../../common/helpers/filter';
import type { StatisticsFilter } from '../../../common/types/statistics/statistics';
import {
  InterventionCenterGovStatistics,
  InterventionCenterGovFaxStatistic,
  InterventionCenterGovOptionStatistic,
  InterventionCenterGovMeasureStatistic,
} from '../../../common/types/statistics/fhbfStatistics';
import statistics from '../../../common/helpers/statistics';
import faxForm from '../../../clients/detail/fhbfForm/fax/faxForm';
import { Field } from '../../../common/types/form';
import policeMeasureForm from '../../../clients/detail/fhbfForm/interventionCenter/policeMeasureForm';
import contactForm from '../../../clients/detail/fhbfForm/interventionCenter/contactForm';
import anamnesisForm from '../../../clients/detail/fhbfForm/interventionCenter/anamnesisForm';
import onwardPlacementForm from '../../../clients/detail/fhbfForm/interventionCenter/onwardPlacementForm';
import { Documentation } from '../../../common/types/documentation';
import { didMeasureTakePlace, isClientRelevantForStatistic, isMeasureCounseling } from '../helpers';

const clientOptionsKeyFieldsMapping: {
  key: keyof InterventionCenterGovOptionStatistic;
  fields: Field<Client>[];
}[] = [
  {
    key: 'policeMeasure',
    fields: policeMeasureForm.fields,
  },
  {
    key: 'typeOfInitialContact',
    fields: contactForm.fields,
  },
  {
    key: 'age',
    fields: anamnesisForm.fields,
  },
  {
    key: 'origin',
    fields: anamnesisForm.fields,
  },
  {
    key: 'personExercisingViolence',
    fields: anamnesisForm.fields,
  },
  {
    key: 'onwardPlacement',
    fields: onwardPlacementForm.fields,
  },
];

const getFilteredClients = (
  clients: DecryptedItem<Client>[],
  statisticsFilter: StatisticsFilter,
): DecryptedItem<Client>[] => {
  return clients
    .filter((client) => isClientRelevantForStatistic(client, statisticsFilter.year, statisticsFilter.advisor))
    .map((client) => {
      const measures = (client.payload.measures || []).filter((measure) =>
        filter.isInYear(measure.date, statisticsFilter.year),
      );
      return {
        ...client,
        payload: {
          ...client.payload,
          measures,
        },
      };
    });
};

const getShortContacts = (documentation: Documentation[], statisticsFilter: StatisticsFilter): number => {
  const shortContacts = documentation.filter((docu) => {
    const isInDateRange = filter.isInYear(docu.date, statisticsFilter.year);
    const isShortContacts = docu.type === 'shortContact';

    return isInDateRange && isShortContacts;
  });

  return shortContacts.reduce((sum, docu) => sum + (docu.numberOfShortContact || 0), 0);
};

const getClientOptionStatistic = (clients: DecryptedItem<Client>[]): InterventionCenterGovOptionStatistic => {
  return clientOptionsKeyFieldsMapping.reduce(
    (optionStatistic, { key, fields }) => ({
      ...optionStatistic,
      [key]: statistics.getStatisticByOptions(clients, key, fields),
    }),
    {
      policeMeasure: {},
      typeOfInitialContact: {},
      age: {},
      origin: {},
      personExercisingViolence: {},
      onwardPlacement: {},
    },
  );
};

const getFaxesStatistics = (
  clients: DecryptedItem<Client>[],
  statisticsFilter: StatisticsFilter,
): InterventionCenterGovFaxStatistic => {
  const filteredFaxes: Fax[] = _.flatten(
    clients.map((client) => {
      const faxes = (client.payload.faxes || []).filter((fax) => filter.isInYear(fax.date, statisticsFilter.year));
      return faxes;
    }),
  );

  const faxesPerPoliceStation = statistics.getStatisticByFaxOptions(filteredFaxes, 'policeStation', faxForm.fields);

  return {
    numberOfFaxes: filteredFaxes.length,
    faxesPerPoliceStation,
  };
};

const getContactAttempts = (clients: DecryptedItem<Client>[], statisticsFilter: StatisticsFilter): number => {
  const filteredContactAttempts = _.flatten(
    clients.map((decryptedClient) =>
      (decryptedClient.payload.contactAttempts || []).filter((contactAttempt) =>
        filter.isInYear(contactAttempt.date, statisticsFilter.year),
      ),
    ),
  );

  return filteredContactAttempts.length;
};

const getMeasureStatistics = (
  clients: DecryptedItem<Client>[],
  statisticsFilter: StatisticsFilter,
): InterventionCenterGovMeasureStatistic => {
  const filteredClients = clients
    .filter((decryptedClient) => {
      const measures = (decryptedClient.payload.measures || []).filter(
        (measure: FhbfMeasure) => didMeasureTakePlace(measure) && isMeasureCounseling(measure),
      );
      return measures.length > 0;
    })
    .map((decryptedClient) => {
      const measures = (decryptedClient.payload.measures || []).filter(
        (measure) => didMeasureTakePlace(measure) && isMeasureCounseling(measure),
      );
      return {
        ...decryptedClient,
        payload: {
          ...decryptedClient.payload,
          measures,
        },
      };
    });

  const filteredMeasures: FhbfMeasure[] = _.flatten(
    clients.map((client) => {
      const measures = (client.payload.measures || []).filter(
        (measure) => filter.isInYear(measure.date, statisticsFilter.year) && didMeasureTakePlace(measure),
      );
      return measures;
    }),
  );

  const calculateValue = (hasIncreased: boolean, currentValue: number): number => {
    return hasIncreased ? currentValue + 1 : currentValue;
  };

  const clientMeasureStatistics = filteredClients.reduce(
    (statistics, client) => {
      const numberOfWomenWithOneMeasure = calculateValue(
        (client.payload.measures || []).length === 1,
        statistics.numberOfWomenWithOneMeasure,
      );
      const numberOfWomenWithTwoMeasures = calculateValue(
        (client.payload.measures || []).length === 2,
        statistics.numberOfWomenWithTwoMeasures,
      );
      const numberOfWomenWithThreeMeasures = calculateValue(
        (client.payload.measures || []).length === 3,
        statistics.numberOfWomenWithThreeMeasures,
      );
      const numberOfWomenWithFourMeasures = calculateValue(
        (client.payload.measures || []).length === 4,
        statistics.numberOfWomenWithFourMeasures,
      );
      const numberOfWomenWithFiveAndMoreMeasures = calculateValue(
        (client.payload.measures || []).length >= 5,
        statistics.numberOfWomenWithFiveAndMoreMeasures,
      );
      return {
        numberOfWomenWithOneMeasure,
        numberOfWomenWithTwoMeasures,
        numberOfWomenWithThreeMeasures,
        numberOfWomenWithFourMeasures,
        numberOfWomenWithFiveAndMoreMeasures,
      };
    },
    {
      numberOfWomenWithOneMeasure: 0,
      numberOfWomenWithTwoMeasures: 0,
      numberOfWomenWithThreeMeasures: 0,
      numberOfWomenWithFourMeasures: 0,
      numberOfWomenWithFiveAndMoreMeasures: 0,
    },
  );

  const measureStatistics = filteredMeasures.reduce(
    (statistic, measure) => {
      const numberOfTypeTelephoneConsultation = calculateValue(
        measure.type === 'telephoneConsultation',
        statistic.numberOfTypeTelephoneConsultation,
      );
      const numberOfTypePersonalConsultation = calculateValue(
        measure.type === 'personalConsultation',
        statistic.numberOfTypePersonalConsultation,
      );
      const numberOfTypeOnlineConsultation = calculateValue(
        measure.type === 'onlineConsultation',
        statistic.numberOfTypeOnlineConsultation,
      );
      const numberOfTypeFollowUp = calculateValue(
        measure.type === 'followUp',
        statistic.numberOfTypeOnlineConsultation,
      );
      const numberOfTypeOutreachConsultation = calculateValue(
        measure.type === 'outreachConsultation',
        statistic.numberOfTypeOutreachConsultation,
      );
      const numberOfTypeAccompaniment = calculateValue(
        measure.type === 'accompaniment',
        statistic.numberOfTypeOutreachConsultation,
      );
      const numberOfConsultationWithTranslator = calculateValue(
        measure.translator === true,
        statistic.numberOfConsultationWithTranslator,
      );

      return {
        numberOfTypeTelephoneConsultation,
        numberOfTypePersonalConsultation,
        numberOfTypeOnlineConsultation,
        numberOfTypeFollowUp,
        numberOfTypeOutreachConsultation,
        numberOfTypeAccompaniment,
        numberOfConsultationWithTranslator,
      };
    },
    {
      numberOfTypeTelephoneConsultation: 0,
      numberOfTypePersonalConsultation: 0,
      numberOfTypeOnlineConsultation: 0,
      numberOfTypeFollowUp: 0,
      numberOfTypeOutreachConsultation: 0,
      numberOfTypeAccompaniment: 0,
      numberOfConsultationWithTranslator: 0,
    },
  );

  const totalNumberOfMeasures = filteredMeasures.filter(
    (measure) => didMeasureTakePlace(measure) && isMeasureCounseling(measure),
  ).length;

  return {
    totalNumberOfMeasures,
    numberOfWomenAdvised: filteredClients.length,
    ...measureStatistics,
    ...clientMeasureStatistics,
  };
};

const createInterventionCenterGovStatistics = (
  clients: DecryptedItem<Client>[],
  documentation: Documentation[],
  statisticsFilter: StatisticsFilter,
): InterventionCenterGovStatistics => {
  const filteredClients = getFilteredClients(clients, statisticsFilter);
  const numberOfShortContacts = getShortContacts(documentation, statisticsFilter);

  const faxesStatistic = getFaxesStatistics(filteredClients, statisticsFilter);
  const measureStatistic = getMeasureStatistics(filteredClients, statisticsFilter);
  const contactAttempts = getContactAttempts(clients, statisticsFilter);
  const optionClientStatistic = getClientOptionStatistic(filteredClients);

  const numberOfSelfReporter = statistics.getNumberOfValues(filteredClients, 'selfReporter');
  const numberOfRepeatCases = statistics.getNumberOfValues(filteredClients, 'repeatCase');
  const numberOfChildren = statistics.getNumberOfValues(filteredClients, 'children');
  const numberOfPregnancy = statistics.getNumberOfValues(filteredClients, 'pregnancy');
  const numberOfWomenWithNoConsultationRequest = statistics.getNumberOfValues(filteredClients, 'noConsultationRequest');
  const numberOfDomesticViolence = statistics.getNumberOfValues(filteredClients, 'domesticViolence');
  const numberOfExPartnerStalking = statistics.getNumberOfValues(filteredClients, 'exPartnerStalking');
  const sumOfChildrenZeroToThreeYears = statistics.getSumOfValues(filteredClients, 'ageOfChildrenZeroToThreeYears');
  const sumOfChildrenFourToSixYears = statistics.getSumOfValues(filteredClients, 'ageOfChildrenFourToSixYears');
  const sumOfChildrenSevenToThirteenYears = statistics.getSumOfValues(
    filteredClients,
    'ageOfChildrenSevenToThirteenYears',
  );
  const sumOfChildrenFourteenToSeventeenYears = statistics.getSumOfValues(
    filteredClients,
    'ageOfChildrenFourteenToSeventeenYears',
  );

  const clientsWithOnwardPlacementDirectContact = filteredClients.filter(
    ({ payload }) =>
      payload.onwardPlacementDirectContact === true &&
      (payload.onwardPlacementContactDetails === undefined || payload.onwardPlacementContactDetails === false) &&
      (payload.onwardPlacement === undefined || payload.onwardPlacement.length === 0),
  );
  const clientsWithOnwardPlacementContactDetails = filteredClients.filter(
    ({ payload }) =>
      (payload.onwardPlacementDirectContact === undefined || payload.onwardPlacementDirectContact === false) &&
      (payload.onwardPlacementContactDetails === true ||
        (payload.onwardPlacement !== undefined && payload.onwardPlacement.length > 0)),
  );
  const clientsWithBothOnwardPlacement = filteredClients.filter(
    ({ payload }) =>
      payload.onwardPlacementDirectContact === true &&
      (payload.onwardPlacementContactDetails === true ||
        (payload.onwardPlacement !== undefined && payload.onwardPlacement.length > 0)),
  );

  const numberOfClientsWithOnwardPlacement =
    clientsWithOnwardPlacementDirectContact.length +
    clientsWithOnwardPlacementDirectContact.length +
    clientsWithBothOnwardPlacement.length;

  return {
    type: 'interventionCenterGov',
    numberOfSelfReporter,
    numberOfRepeatCases,
    numberOfChildren,
    numberOfPregnancy,
    numberOfWomenWithNoConsultationRequest,
    sumOfChildrenZeroToThreeYears,
    sumOfChildrenFourToSixYears,
    sumOfChildrenSevenToThirteenYears,
    sumOfChildrenFourteenToSeventeenYears,
    numberOfDomesticViolence,
    numberOfExPartnerStalking,
    numberOfClientsWithOnwardPlacement,
    numberOfOnwardPlacementDirectContact: clientsWithOnwardPlacementDirectContact.length,
    numberOfOnwardPlacementContactDetails: clientsWithOnwardPlacementContactDetails.length,
    numberOfOnwardPlacement: clientsWithBothOnwardPlacement.length,
    numberOfContactAttempts: contactAttempts + numberOfShortContacts,
    ...faxesStatistic,
    ...measureStatistic,
    ...optionClientStatistic,
  };
};

export default createInterventionCenterGovStatistics;
